const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TOKEN_PATTERN = /^\d{6}$/;

export const LATEST_EMAIL_COPY =
  "Use this latest email. Older Activate links no longer work.";

export const CLUB_ALREADY_EXISTS = "Club already exists";
export const USER_ALREADY_EXISTS = "User already exists";
export const OWNER_ALREADY_HAS_CLUB =
  "This email already has a club. Sign in instead of creating another one.";
export const PHONE_VERIFICATION_REQUIRED =
  "Verify your phone number before creating a club.";
export const UNCONFIRMED_SIGNUP_WAIT =
  "We already sent a confirmation email. Use that, or wait 15 minutes to start over.";
export const TOO_MANY_REQUESTS =
  "Too many requests. Please wait a moment before trying again.";

export function apiUserMessage(error) {
  const data = error?.response?.data ?? {};
  const raw = data.detail || data.Message || data.Data || "";
  return typeof raw === "string" ? raw : "";
}

export function activationFailureMessage(error) {
  const data = error?.response?.data ?? {};
  if (
    data.errorCode === "CodeMismatchException" ||
    data.errorCode === "ExpiredCodeException" ||
    data.detail === LATEST_EMAIL_COPY
  ) {
    return LATEST_EMAIL_COPY;
  }
  if (error?.response?.status === 409) {
    return OWNER_ALREADY_HAS_CLUB;
  }
  const raw = apiUserMessage(error);
  if (String(raw).includes("already has a club")) {
    return OWNER_ALREADY_HAS_CLUB;
  }
  if (raw === CLUB_ALREADY_EXISTS || String(raw).includes("Failed to save club")) {
    return CLUB_ALREADY_EXISTS;
  }
  return raw || "Verification failed";
}

export function normalizeVerificationEmail(email) {
  return String(email ?? "").trim().toLowerCase();
}

export function isValidVerificationEmail(email) {
  return EMAIL_PATTERN.test(normalizeVerificationEmail(email));
}

export function isValidVerificationToken(token) {
  return TOKEN_PATTERN.test(String(token ?? "").trim());
}

export function parseEmailVerificationSearch(search) {
  const params = new URLSearchParams(
    String(search ?? "").startsWith("?") ? search : `?${search ?? ""}`,
  );
  return {
    email: normalizeVerificationEmail(params.get("email")),
    token: String(params.get("token") ?? "").trim(),
  };
}

export async function checkEmailAvailable(email, emailExists) {
  const normalized = normalizeVerificationEmail(email);
  if (!isValidVerificationEmail(normalized)) {
    return { ok: false, message: "Invalid email" };
  }
  try {
    const result = await emailExists(normalized);
    if (result?.Data === true) {
      return { ok: false, message: USER_ALREADY_EXISTS };
    }
    return { ok: true, email: normalized };
  } catch (error) {
    if (error?.response?.status === 400) {
      return { ok: false, message: "Invalid email" };
    }
    throw error;
  }
}

export function registrationConflictMessage(error) {
  const status = error?.response?.status;
  const raw = apiUserMessage(error);
  if (status === 409 || String(raw).toLowerCase().includes("user already exists")) {
    return USER_ALREADY_EXISTS;
  }
  return "";
}

export function rateLimitFailureMessage(error) {
  if (error?.response?.status !== 429) {
    return "";
  }
  return apiUserMessage(error) || TOO_MANY_REQUESTS;
}

export function clubRegistrationFailureMessage(error) {
  const conflict = registrationConflictMessage(error);
  if (conflict) {
    return conflict;
  }
  const limited = rateLimitFailureMessage(error);
  if (limited) {
    return apiUserMessage(error) || UNCONFIRMED_SIGNUP_WAIT;
  }
  const raw = apiUserMessage(error);
  if (
    error?.response?.status === 400 &&
    (!raw || raw === PHONE_VERIFICATION_REQUIRED || /phone/i.test(raw))
  ) {
    return raw || PHONE_VERIFICATION_REQUIRED;
  }
  return raw;
}

export function isEmailVerificationLink(search) {
  const { email, token } = parseEmailVerificationSearch(search);
  return isValidVerificationEmail(email) && isValidVerificationToken(token);
}
